// src/pages/onboarding/ai-run.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  SearchX,
  Target,
  BarChart3,
  TrendingUp,
  Bug,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { useOnboarding } from "@/contexts/onboarding-context";
import { getOnboardingRedirectPath } from "@/lib/onboarding-navigation-utils";

const DEBUG = process.env.NODE_ENV === "development";

const insightOptions = [
  {
    id: "keyword-ngram",
    title: "Keyword N-gram Analysis",
    description:
      "Analyze keyword performance patterns to identify high-performing n-grams and optimization opportunities.",
    icon: <BarChart3 className="w-12 h-12 text-primary" />,
    link: "/onboarding/ai-result/keyword-ngram",
  },
  {
    id: "search-term-patterns",
    title: "Search Term Pattern Analysis",
    description:
      "Analyze converting search terms to identify user intent patterns and language that resonates with customers.",
    icon: <TrendingUp className="w-12 h-12 text-primary" />,
    link: "/onboarding/ai-result/search-term-patterns",
  },
  {
    id: "search-term-ngram",
    title: "Search Term N-gram Analysis",
    description:
      "Analyze search term n-grams to identify user intent patterns and find negative keyword opportunities.",
    icon: <SearchX className="w-12 h-12 text-primary" />,
    link: "/onboarding/ai-result/search-term-ngram",
  },
  {
    id: "keyword-expansion",
    title: "Keyword Expansion Opportunities",
    description:
      "Discover new keyword themes and expansion opportunities based on your highest-performing keywords.",
    icon: <Target className="w-12 h-12 text-primary" />,
    link: "/onboarding/ai-result/keyword-expansion",
  },
];

const OnboardingAiRunPage = () => {
  const navigate = useNavigate();
  const [isDebugMode, setIsDebugMode] = useState(false);
  const { data, hasDataBeenFetched } = useOnboardingData();
  const { webAppUrl } = useOnboarding();

  // Get debug mode from localStorage
  useEffect(() => {
    const debugMode = localStorage.getItem("onboarding-debug-mode") === "true";
    setIsDebugMode(debugMode);
  }, []);

  const goToResults = () => {
    navigate("/onboarding/results");
  };

  // Check if data is available
  if (!hasDataBeenFetched || !data) {
    const redirect = getOnboardingRedirectPath(webAppUrl, !!data);

    return (
      <main className="flex-1 flex items-center justify-center p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>{redirect.message}</p>
            <Button asChild>
              <Link to={redirect.path}>Continue</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-800 mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Using Mike's AI account for your trial
            </div>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Let's Generate Your
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                First Insight
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose any of the analyses below. I'll use the data from your
              account to generate custom recommendations for you. You can add
              your own AI account later for additional insights.
            </p>
          </div>

          {/* Options Grid */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            {insightOptions.map((option, index) => (
              <div
                key={option.id}
                style={{ animationDelay: `${300 + index * 100}ms` }}
              >
                <Link
                  to={option.link}
                  className="hover:scale-105 transform transition-all duration-300 cursor-pointer animate-scale-in block"
                >
                  <Card className="h-full bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large hover:shadow-xl transition-all duration-300">
                    <CardContent className="flex flex-col items-center text-center p-8">
                      <div className="mb-6">{option.icon}</div>
                      <CardTitle className="text-xl font-semibold mb-4">
                        {option.title}
                      </CardTitle>
                      <CardDescription className="text-base leading-relaxed">
                        {option.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      {DEBUG && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Bug className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Debug Panel</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDebugMode}
                    onCheckedChange={(checked) => {
                      setIsDebugMode(checked);
                      localStorage.setItem(
                        "onboarding-debug-mode",
                        checked.toString(),
                      );
                    }}
                    id="debug-mode"
                  />
                  <label htmlFor="debug-mode" className="text-sm">
                    Debug Mode
                  </label>
                </div>
                <Button
                  onClick={goToResults}
                  variant="outline"
                  size="sm"
                  className="text-xs w-full"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Go to Results
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OnboardingAiRunPage;
